'use client';

import React from 'react';
import { motion } from 'framer-motion';
import { LuStethoscope, LuShoppingBag, LuMapPin } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { scrollToTop } from '../../utils/scrollUtils';
import {
  GUTTER, PANEL_GAP, SURFACE, CARD_SHADOW,
  DotGrid, RedSurfaceDecor, Eyebrow, SectionTitle, PillButton,
} from './landingUi';

// The key names are historical; the texts are "Persönlich beraten",
// "Einfach bestellen" and "Für Sie vor Ort".
const VALUE_ITEMS = [
  {
    id: 'trusted',
    Icon: LuStethoscope,
    titleKey: 'home.valuePropositions.trustedExpertise.title',
    descriptionKey: 'home.valuePropositions.trustedExpertise.description',
  },
  {
    id: 'tailored',
    Icon: LuShoppingBag,
    titleKey: 'home.valuePropositions.tailoredSolutions.title',
    descriptionKey: 'home.valuePropositions.tailoredSolutions.description',
  },
  {
    id: 'impact',
    Icon: LuMapPin,
    titleKey: 'home.valuePropositions.provenImpact.title',
    descriptionKey: 'home.valuePropositions.provenImpact.description',
  },
];

/**
 * Value propositions as a bento row: the heading on the left, three cards on
 * the right. The cards rest in one colour (the off-white surface); the one
 * under the pointer lifts and turns brand red, so the row has one loud beat
 * that follows the mouse.
 *
 * On phones the three cards become a swipeable row (each ~80% of the screen,
 * so the next one peeks in); from `sm` up they sit in a three-column grid.
 */
const WhyChooseUs = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  return (
    <section className={`${GUTTER} ${PANEL_GAP}`} aria-label={t('home.valuePropositions.eyebrow') || 'Value propositions'}>
      <div className="relative overflow-hidden rounded-[2rem] bg-white px-6 py-[clamp(2rem,4.5vw,4.5rem)] ring-1 ring-black/[0.04] sm:px-[clamp(1.5rem,3.5vw,4rem)] lg:rounded-[2.5rem]">
        <DotGrid radius="rounded-[2rem] lg:rounded-[2.5rem]" maskImage="radial-gradient(ellipse 45% 70% at 0% 100%, #000, transparent)" />

        <div className="relative grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-[clamp(1rem,2vw,2rem)]">
          {/* Heading */}
          <motion.div
            className="flex flex-col justify-between lg:col-span-4"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            <div>
              <Eyebrow>{t('home.valuePropositions.eyebrow') || 'Ihre Vorteile'}</Eyebrow>
              <SectionTitle
                className="mt-5"
                lead={t('home.valuePropositions.title') || 'Gesundheit,'}
                highlight={t('home.valuePropositions.titleHighlight') || 'einfach gemacht.'}
              />
              <p className="mt-5 max-w-sm text-[15px] leading-relaxed text-gray-500 xl:text-base">
                {t('home.valuePropositions.subtitle')}
              </p>
            </div>
            <div className="mt-6 lg:mt-8">
              <PillButton onClick={() => { navigate('/products'); setTimeout(() => scrollToTop(), 100); }}>
                {t('hero.ctaDiscover')}
              </PillButton>
            </div>
          </motion.div>

          {/* Cards */}
          <div className="why-rail -mx-6 flex snap-x snap-mandatory scroll-px-6 gap-3 overflow-x-auto px-6 pb-2 sm:mx-0 sm:grid sm:grid-cols-3 sm:gap-[clamp(0.75rem,1.2vw,1.25rem)] sm:overflow-visible sm:px-0 sm:pb-0 lg:col-span-8">
            <style>{`.why-rail { scrollbar-width: none; } .why-rail::-webkit-scrollbar { display: none; }`}</style>
            {VALUE_ITEMS.map(({ id, Icon, titleKey, descriptionKey }, index) => (
              <motion.article
                key={id}
                className={`group relative flex min-h-[17rem] w-[78vw] max-w-[20rem] flex-shrink-0 snap-start flex-col justify-between overflow-hidden rounded-[2rem] p-[clamp(1.25rem,1.8vw,2rem)] ring-1 ring-black/[0.04] transition-shadow duration-300 [--stroke:rgb(var(--brand))] hover:ring-transparent hover:[--stroke:rgba(255,255,255,0.75)] landing:[--stroke:#E33B4C] landing:hover:[--stroke:rgba(255,255,255,0.75)] sm:min-h-[21rem] sm:w-auto sm:max-w-none ${CARD_SHADOW} hover:shadow-[0_34px_60px_-30px_rgb(var(--brand)/0.75)]`}
                style={{ backgroundColor: SURFACE }}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.5, delay: 0.08 * index, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -10, scale: 1.02 }}
              >
                {/* Resting: sand with the dot grid. Hover: the red surface fades in over it. */}
                <DotGrid radius="rounded-[2rem]" />
                <div aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-[2rem] bg-brand opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                  <RedSurfaceDecor radius="rounded-[2rem]" />
                </div>

                <div className="relative flex items-start justify-between">
                  <span
                    className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-brand ring-1 ring-black/[0.06] transition-transform duration-300 group-hover:-rotate-6 group-hover:scale-105 group-hover:ring-transparent"
                    aria-hidden="true"
                  >
                    <Icon className="h-6 w-6" strokeWidth={1.8} />
                  </span>
                  <span
                    aria-hidden="true"
                    className="select-none text-[2.75rem] font-black leading-none tracking-tighter text-transparent transition-[-webkit-text-stroke-color] duration-300"
                    style={{ WebkitTextStroke: '1.5px var(--stroke)' }}
                  >
                    {String(index + 1).padStart(2, '0')}
                  </span>
                </div>

                <div className="relative mt-8 sm:mt-10">
                  <h3 className="text-[clamp(1.25rem,1.6vw,1.6rem)] font-extrabold leading-[1.1] tracking-[-0.03em] text-gray-900 transition-colors duration-300 group-hover:text-white">
                    {t(titleKey)}
                  </h3>
                  <p className="mt-3 text-[14px] leading-relaxed text-gray-500 transition-colors duration-300 group-hover:text-white/85">
                    {t(descriptionKey)}
                  </p>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
